import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    FeatureBlockContent,
    FeatureBlockSection
} from '../FeatureBlockElements';
import HBText from '../honeybook-ui/HBText/HBText';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import HBInput from '../honeybook-ui/HBInput/HBInput';
import MarqueeAnimation from '../MarqueeAnimation/MarqueeAnimation';
import SuggestionPreview from './SuggestionPreview';
import HBVideo from '../honeybook-ui/HBVideo/HBVideo';
import AnalyticsService from '../../Services/AnalyticsService';
import { useVimeoPlayer } from '../../hooks/useVimeoPlayer';

import './broad-intent-typewriter-animation.scss';
import './broad-intent-chat-hero.scss';

const SUGGESTION_ITEMS = [
    {
        transKey: 'automations',
        iconUrl: 'marketing_site/paid/broad_intent_test/Cog16.svg'
    },
    {
        transKey: 'invoices',
        iconUrl: 'marketing_site/paid/broad_intent_test/Dollar16.svg'
    },
    {
        transKey: 'contracts',
        iconUrl: 'marketing_site/paid/broad_intent_test/Signature16.svg'
    },
    {
        transKey: 'leads',
        iconUrl: 'marketing_site/paid/broad_intent_test/Leadforms16.svg'
    },
    {
        transKey: 'proposals',
        iconUrl: 'marketing_site/paid/broad_intent_test/Proposal16.svg'
    },
    {
        transKey: 'onboarding',
        iconUrl: 'marketing_site/paid/broad_intent_test/User16.svg'
    },
    {
        transKey: 'scheduler',
        iconUrl: 'marketing_site/paid/broad_intent_test/CalendarPlus16.svg'
    }
];

const MARQUEE_OPTIONS = {
    gap: 20,
    speed: 50,
    reverse: false
};

const BroadIntentChatHero = () => {
    const { t } = useTranslation();
    const [wasFirstTextboxInteraction, setWasFirstTextboxInteraction] =
        useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedSuggestionTransKey, setSelectedSuggestionTransKey] =
        useState<string>('');
    const inputRef = useRef<HBInput>(null);
    const videoContainerRef = useRef<HTMLDivElement>(null);

    useVimeoPlayer({
        containerRef: videoContainerRef,
        videoId: '1092039982',
        onPlay: () => {
            AnalyticsService.trackClick('watch video', {
                broad_intent_get_started_hero: 'broad_intent_1_chat',
                app_path: window.location.pathname
            });
        }
    });

    useEffect(() => {
        onInputWrapperClick();
    }, []);

    const onInputWrapperClick = () => {
        if (inputRef.current?.inputRef.current) {
            inputRef.current.inputRef.current.focus();
        }
    };

    const handleSuggestionClick = (transKey: string) => {
        if (selectedSuggestionTransKey !== transKey) {
            AnalyticsService.track('action: textbox prompt initiated', {
                broad_intent_get_started_hero: 'broad_intent_1_chat',
                action_type: 'chose default prompt',
                user_choice: t(
                    `broadIntentContent.hero.chat.suggestions.items.${transKey}`
                )
            });
            setSelectedSuggestionTransKey(transKey);
            setInputValue(
                t(`broadIntentContent.hero.chat.suggestions.items.${transKey}`)
            );
        } else {
            setInputValue('');
            setSelectedSuggestionTransKey('');
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!wasFirstTextboxInteraction) {
            setWasFirstTextboxInteraction(true);
            AnalyticsService.track('action: textbox prompt initiated', {
                broad_intent_get_started_hero: 'broad_intent_1_chat',
                action_type: 'started typing',
                user_choice: null
            });
        }
        setInputValue(e.target.value);
        if (selectedSuggestionTransKey) {
            setSelectedSuggestionTransKey('');
        }
    };

    const panels = useMemo(() => {
        return SUGGESTION_ITEMS.map(item => (
            <SuggestionPreview
                key={item.transKey}
                title={t(
                    `broadIntentContent.hero.chat.suggestions.items.${item.transKey}`
                )}
                iconUrl={item.iconUrl}
                onClick={() => handleSuggestionClick(item.transKey)}
                isSelected={
                    !!inputValue && selectedSuggestionTransKey === item.transKey
                }
            />
        ));
    }, [t, selectedSuggestionTransKey, inputValue]);

    return (
        <>
            <FeatureBlockSection customClass="broad-intent-hero--chat">
                <FeatureBlockContent customClass="broad-intent-hero--chat__form-content">
                    <HBText
                        customClass="broad-intent-hero--chat__title"
                        tag="h1"
                        type="title-l"
                    >
                        <Trans
                            t={t}
                            i18nKey="broadIntentContent.hero.chat.title"
                        >
                            <HBText tag="span" type="title-l"></HBText>
                        </Trans>
                    </HBText>
                    <HBText
                        customClass="broad-intent-hero--chat__subtitle"
                        tag="h3"
                        type="body-xl"
                    >
                        {t('broadIntentContent.hero.chat.subtitle') as string}
                    </HBText>

                    <div className="broad-intent-hero--chat__form">
                        <div
                            className="broad-intent-hero--chat__input-wrapper"
                            onClick={onInputWrapperClick}
                        >
                            <HBInput
                                ref={inputRef}
                                customClass="broad-intent-hero--chat__input"
                                inputStyle="simple-input"
                                isValid
                                value={inputValue}
                                onChange={handleInputChange}
                            />

                            <RegistrationForm
                                direction="row"
                                source="broad-intent-hero-chat"
                                customClass="broad-intent-hero--chat__cta"
                                buttonText={t(
                                    'broadIntentContent.hero.chat.form.buttonText'
                                )}
                                icon="nx-arrow-line-right-16"
                            />
                        </div>
                    </div>
                </FeatureBlockContent>

                <div className="broad-intent-hero--chat__suggestions">
                    <HBText
                        customClass="broad-intent-hero--chat__suggestions-title"
                        tag="h3"
                        type="body-xs"
                    >
                        {
                            t(
                                'broadIntentContent.hero.chat.suggestions.title'
                            ) as string
                        }
                    </HBText>

                    <MarqueeAnimation
                        options={MARQUEE_OPTIONS}
                        panels={panels}
                    />
                </div>
            </FeatureBlockSection>

            <FeatureBlockSection customClass="broad-intent-hero--chat__video-section">
                <FeatureBlockContent customClass="broad-intent-hero--chat__video-content">
                    <div ref={videoContainerRef}>
                        <HBVideo
                            autoplay={false}
                            controls
                            muted={false}
                            videoId={'1092039982'}
                            customClass="broad-intent-hero--chat__video"
                            ratio={415 / 750}
                        />
                    </div>
                </FeatureBlockContent>
            </FeatureBlockSection>
        </>
    );
};

export default BroadIntentChatHero;
